import { useMemo, useState, useEffect } from 'react';
import { CartEarningPanel, useMyEarnings } from '../../components/SchemeEarning';
import { Select, Button, InputNumber, useApp, Checkbox, Tooltip, Carousel, Image, Input } from '../../ui';
import { Trash2, Plus, Minus, ShoppingCart, Image as ImageIcon, Eye } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRetailerSearch } from '../../hooks/useRetailerSearch';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { request } from '../../api/client';
import { PROFORMA_ON_APPROVAL_ENABLED } from '../../lib/flags';
import { usePermission, useAuthStore } from '../../store/auth';
import { fmtPackQty, fmtUom, fmtQty, inr2 } from '../../lib/format';
import { displayBoxLoose, hasBoxPacking, boxLoosePhrase } from '../../lib/packaging';
import { toImageUrls } from '../../lib/imageUrls';
import { PayOrderModal } from './PayOrderModal';
import { AdvancePayPromptModal } from './AdvancePayPromptModal';
import { customerOption, CUSTOMER_SELECT_PROPS } from '../../components/CustomerOption';
import { inr as inrWhole } from '../../lib/format';

// The shared formatter: a negative reads "-₹480", never "₹-480".
const inr = inrWhole;
// Exact-paise money — paise shown only when the amount has them, so ₹9,080.10 keeps both digits
// (never "₹9,080.1") while a whole figure reads "₹9,080" rather than "₹9,080.00".

interface CartLine {
  productId: string;
  sku: string;
  name: string;
  variantLabel?: string | null;
  uom: string;
  packSize?: number | null; // how much ONE pack holds, in `uom` (500 ml bottle → 0.5 LTR); qty counts packs
  qty: number;              // total base units for a units-only (no-packing) line
  unitPrice: number;
  mrp: number;
  discountPct: number;
  gstPct: number;
  stockTotal: number;       // base units on hand
  // Box packing (per variant). For a box-packed line the qty is derived: box × unitsPerBox + loose.
  boxPackingEnabled?: boolean;
  unitsPerBox?: number | null;
  boxQty?: number;
  looseQty?: number;
}

// The authoritative total base units for a cart line (box-packed → box × unitsPerBox + loose; else qty).
const lineUnits = (l: CartLine): number =>
  l.boxPackingEnabled && Number(l.unitsPerBox) > 0
    ? (Number(l.boxQty) || 0) * Number(l.unitsPerBox) + (Number(l.looseQty) || 0)
    : l.qty;

export function NewOrderPage() {
  const { message } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  // When opened from an active field visit (Field Day → New Order), the retailer is
  // fixed and the placed order is linked back to that visit.
  const visitId = searchParams.get('visitId') || undefined;
  /** Back where the order was started from, so placing one returns you to the work you were doing —
   *  the lead you just converted, or the field day you are in the middle of. */
  const backTo = visitId ? '/ffa/my-day' : '/orders';
  const [retailerId, setRetailerId] = useState<string | undefined>(searchParams.get('retailerId') || undefined);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [search, setSearch] = useState('');
  const [uomFilter, setUomFilter] = useState<string | undefined>();
  const [stockFilter, setStockFilter] = useState<'all' | 'in' | 'out'>('all');
  const [overrideCredit, setOverrideCredit] = useState(false);
  const [placing, setPlacing] = useState(false);
  // Advance payment: clicking "Place Order" previews the before-billing CD for the CART, then opens a
  // confirmation/advance prompt — the order is created only when the user commits inside it (Place order /
  // Pay & place). Closing the prompt (X/Cancel) creates nothing. `promptData` holds the pending order
  // payload + its advance-CD preview while the prompt is open; `payFor` opens the pay modal afterward.
  const [promptData, setPromptData] = useState<{ payload: any; est: any } | null>(null);
  const [payFor, setPayFor] = useState<{ id: string; orderNo: string } | null>(null);
  const canOverride = usePermission('RETAILER.CREDIT.OVERRIDE');
  const canOverrideDiscount = usePermission('ORDER.DISCOUNT.OVERRIDE');
  const canPayOrder = usePermission('ORDER.PAY');
  // A retailer ordering for themselves: no retailer picker (they can't order for
  // another shop), and the server fills the retailer from their login.
  const isRetailer = useAuthStore((s) => s.user?.role) === 'RETAILER';
  const { data: myEarnings } = useMyEarnings(); // null for a dealer on no scheme (and for staff)

  // The retailer picker searches the server (see useRetailerSearch); the picked or visit-preselected
  // retailer stays resolvable even when a later search no longer returns it.
  const { rows: retailerRows, onSearch: onRetailerSearch, rowFor: retailerRow } = useRetailerSearch({
    key: 'retailers-for-order',
    enabled: !isRetailer, // retailers don't have RETAILER.VIEW; skip the (forbidden) call
    selected: retailerId,
  });
  const { data: catalog, isLoading: catalogLoading } = useQuery({
    queryKey: ['catalog-cached'],
    queryFn: () => request<any[]>({ url: '/catalog/with-stock' }),
  });
  // A retailer placing their OWN order can't fetch the retailer list, so load just their own profile —
  // this carries their discount policy (GENERAL % etc.) so the cart matches the server-priced order.
  const { data: me } = useQuery({
    queryKey: ['my-retailer'],
    queryFn: () => request<any>({ url: '/retailers/me' }),
    enabled: isRetailer,
  });

  // Resolve a retailer's discount policy: GENERAL retailers carry a fixed % that is applied to every line;
  // CUSTOM retailers leave the per-line discount editable. For a retailer self-order it's their own profile.
  const policyFor = (rid?: string) => {
    const r = isRetailer ? me : retailerRow(rid);
    return { isGeneral: r?.discountType === 'GENERAL', pct: Math.max(0, Math.min(100, Number(r?.generalDiscountPct ?? 0))) };
  };

  // Retailer self-order: once their profile loads, apply their GENERAL discount to any items already in the
  // cart (mirrors the staff retailer-picker re-apply) so the cart total matches the server-priced order.
  useEffect(() => {
    if (!isRetailer || !me) return;
    const isGeneral = me.discountType === 'GENERAL';
    const pct = Math.max(0, Math.min(100, Number(me.generalDiscountPct ?? 0)));
    setCart((c) => c.map((l) => ({ ...l, discountPct: isGeneral ? pct : (canOverrideDiscount ? l.discountPct : 0) })));
  }, [isRetailer, me, canOverrideDiscount]);

  // ── Cart helpers ──
  const cartById = useMemo(() => new Map(cart.map((l) => [l.productId, l])), [cart]);
  const addToCart = (p: any) => {
    if (cartById.has(p.productId)) return;
    const { isGeneral, pct } = policyFor(retailerId);
    const boxed = hasBoxPacking({ boxPackingEnabled: p.boxPackingEnabled, unitsPerBox: p.unitsPerBox });
    setCart((c) => [...c, {
      productId: p.productId, sku: p.sku, name: p.name, variantLabel: p.variantLabel, uom: p.uom, packSize: Number(p.packSize) > 0 ? Number(p.packSize) : null,
      qty: 1, unitPrice: Number(p.price), mrp: Number(p.mrp),
      // GENERAL retailer → the fixed %. Otherwise a per-line discount only for pricing-authorised
      // users (Admin/SA); retailers & salespersons start at 0 (the server ignores any client discount).
      discountPct: isGeneral ? pct : (canOverrideDiscount ? Number(p.discountPct ?? 0) : 0),
      gstPct: Number(p.gstPct ?? 0), stockTotal: Number(p.stock?.total ?? 0),
      // Box-packed variant → default to 1 box; else the plain unit stepper.
      boxPackingEnabled: boxed, unitsPerBox: boxed ? Number(p.unitsPerBox) : null,
      boxQty: boxed ? 1 : undefined, looseQty: boxed ? 0 : undefined,
    }]);
  };
  const setQty = (productId: string, qty: number) => {
    if (qty <= 0) { setCart((c) => c.filter((l) => l.productId !== productId)); return; }
    setCart((c) => c.map((l) => (l.productId === productId ? { ...l, qty } : l)));
  };
  // Box-packed line: set boxes + loose; remove the line if both hit zero.
  const setBoxLoose = (productId: string, boxQty: number, looseQty: number) => {
    const b = Math.max(0, Math.floor(boxQty || 0));
    const lo = Math.max(0, looseQty || 0);
    if (b === 0 && lo === 0) { setCart((c) => c.filter((l) => l.productId !== productId)); return; }
    setCart((c) => c.map((l) => (l.productId === productId ? { ...l, boxQty: b, looseQty: lo } : l)));
  };
  const setDisc = (productId: string, discountPct: number) =>
    setCart((c) => c.map((l) => (l.productId === productId ? { ...l, discountPct: Math.max(0, Math.min(100, discountPct)) } : l)));
  const removeLine = (productId: string) => setCart((c) => c.filter((l) => l.productId !== productId));

  // UoM options present in the catalog (e.g. KG, LTR, NOS).
  const uomOptions = useMemo(
    () => Array.from(new Set((catalog ?? []).map((c) => c.uom).filter(Boolean))).map((u) => ({ value: u, label: u })),
    [catalog],
  );

  // ── Catalog filtering: search + UoM + stock availability (client-side) ──
  const products = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (catalog ?? []).filter((c) => {
      if (q && !`${c.sku} ${c.name} ${c.variantLabel ?? ''} ${c.hsnCode ?? ''}`.toLowerCase().includes(q)) return false;
      if (uomFilter && c.uom !== uomFilter) return false;
      const total = Number(c.stock?.total ?? 0);
      if (stockFilter === 'in' && !(total > 0)) return false;
      if (stockFilter === 'out' && total !== 0) return false;
      return true;
    });
  }, [catalog, search, uomFilter, stockFilter]);

  // Collapse variants of the same product (group_code, else sku) into ONE card so a
  // multi-variant product isn't shown as several cards.
  const productGroups = useMemo(() => {
    const map = new Map<string, { key: string; name: string; imageUrl?: string; uom: string; variants: any[] }>();
    for (const p of products) {
      const key = (p.groupCode && String(p.groupCode).trim()) || p.sku;
      let g = map.get(key);
      if (!g) { g = { key, name: p.name, imageUrl: p.imageUrl, uom: p.uom, variants: [] }; map.set(key, g); }
      if (!g.imageUrl && p.imageUrl) g.imageUrl = p.imageUrl;
      g.variants.push(p);
    }
    return Array.from(map.values());
  }, [products]);

  // ── Totals (mirrors the server: GST per line at the product's HSN rate; qty = total base units) ──
  const subtotal = cart.reduce((s, l) => s + lineUnits(l) * l.unitPrice, 0);
  const discount = cart.reduce((s, l) => s + (lineUnits(l) * l.unitPrice * l.discountPct) / 100, 0);
  const taxable = subtotal - discount;
  const gst = cart.reduce((s, l) => s + (lineUnits(l) * l.unitPrice * (1 - l.discountPct / 100)) * (l.gstPct / 100), 0);
  // Mirror the server (order.service `price`): the grand total is rounded to the whole rupee and the paise
  // fraction is booked as round-off — so the cart shows the SAME payable the invoice/advance-prompt will.
  // (Previously the cart showed the unrounded ₹…​.80, which disagreed with the ₹…​.00 everywhere downstream.)
  const grandRaw = taxable + gst;
  const grandTotal = Math.round(grandRaw);
  const roundOff = grandTotal - grandRaw;
  // Effective discount %, only for a plain "5% off" label in the summary — the exact rupee amount stays authoritative.
  const discountPctEff = subtotal > 0 ? Math.round((discount / subtotal) * 100) : 0;
  // A single GST rate to label "GST (18%)" when every line shares it; null when mixed → plain "GST".
  const uniformGstPct = cart.length > 0 && cart.every((l) => l.gstPct === cart[0].gstPct) ? cart[0].gstPct : null;

  // Retailer self-order → their own profile (`me`); staff → the picked retailer from the list.
  const retailer = isRetailer ? me : retailerRow(retailerId);
  const creditLimit = Number(retailer?.creditLimit ?? 0);
  const outstanding = Number(retailer?.outstandingBalance ?? 0);
  const available = creditLimit - outstanding;
  const exceedsCredit = !!retailer && grandTotal > available;

  // The per-line discount is settable only by pricing-authorised users (ORDER.DISCOUNT.OVERRIDE →
  // Admin/Super Admin). For everyone else the discount control is hidden entirely and the server
  // ignores any client discount from them regardless of the UI.
  const isGeneralRetailer = retailer?.discountType === 'GENERAL';
  const generalPct = Math.max(0, Math.min(100, Number(retailer?.generalDiscountPct ?? 0)));

  // An OVERRIDE discount is one that ISN'T just the retailer's standard GENERAL policy — those need a
  // reason (traceability), captured here and stored on the order. GENERAL-policy discounts explain
  // themselves (the retailer's own %), so no reason is required for them.
  const [discountReason, setDiscountReason] = useState('');
  const generalWouldGive = isGeneralRetailer ? (subtotal * generalPct) / 100 : 0;
  const isOverrideDiscount = canOverrideDiscount && discount > 0.01 && Math.abs(discount - generalWouldGive) > 0.01;

  // The order payload from the current cart. Built once and reused by the prompt's commit actions and the
  // advance-CD preview so what's previewed is exactly what gets placed.
  const buildPayload = () => ({
    // Retailer self-order omits retailerId — the server fills it from the login.
    ...(isRetailer ? {} : { retailerId }),
    channel: 'WEB',
    ...(visitId ? { visitId } : {}), // link the order to the active field visit
    ...(overrideCredit ? { overrideCredit: true } : {}),
    ...(isOverrideDiscount && discountReason.trim() ? { discountReason: discountReason.trim() } : {}),
    items: cart.map((l) => ({
      productId: l.productId, unitPrice: l.unitPrice, discountPct: l.discountPct,
      // Box-packed line → send boxes + loose (server computes the authoritative total); else a plain unit qty.
      ...(l.boxPackingEnabled && Number(l.unitsPerBox) > 0
        ? { boxQuantity: Number(l.boxQty) || 0, looseUnitQuantity: Number(l.looseQty) || 0 }
        : { qty: l.qty }),
    })),
  });

  // Actually create the order. Returns the created order, or null on failure (error already toasted).
  const submitOrder = async (payload: any): Promise<any | null> => {
    if (placing) return null;
    setPlacing(true);
    try {
      const r = await request<any>({ url: '/orders', method: 'POST', data: payload });
      message.success(`Order ${r.orderNo} placed`);
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['orders-pending-approval'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      qc.invalidateQueries({ queryKey: ['my-day'] });
      return r;
    } catch (e: any) {
      message.error(e.response?.data?.error?.message ?? 'Failed to place order');
      return null;
    } finally {
      setPlacing(false);
    }
  };

  // "Place Order" clicked. For payers we first fetch the advance-CD preview (no order created yet):
  //  • eligible → prompt with the discount + pay option;
  //  • assigned to a scheme but below the tier → prompt with the eligibility criteria + plain Place order;
  //  • no scheme assigned → no prompt, place directly.
  // Non-payers always place directly. Cancelling the prompt creates nothing.
  const place = async () => {
    if (cart.length === 0) { message.warning('Add items to the cart'); return; }
    if (!isRetailer && !retailerId) { message.warning('Pick a retailer'); return; }
    if (isOverrideDiscount && !discountReason.trim()) { message.warning('Enter a reason for the discount'); return; }
    if (placing) return;
    const payload = buildPayload();
    const placeDirect = async () => { const r = await submitOrder(payload); if (r) navigate(backTo); };
    if (!canPayOrder) { await placeDirect(); return; }
    // Peek at the advance-CD offer for this cart to decide whether to prompt.
    setPlacing(true);
    let est: any = null;
    try { est = await request<any>({ url: '/orders/advance-cd/preview', method: 'POST', data: payload }); }
    catch { /* preview unavailable → just place */ }
    setPlacing(false);
    // With PROFORMA_ON_APPROVAL_ENABLED the advance is offered on the PROFORMA, which does not exist
    // until the warehouse accepts the order. Asking here would be asking for money against an order
    // the company has not yet committed to fulfilling — the exact situation this flow removes.
    // The SERVER's setting decides when it reports one (est.proformaOnApproval); the build-time flag is only
    // the fallback for an older server. A mismatch offered "Place & pay" and then refused the payment.
    const proformaOnApproval = typeof est?.proformaOnApproval === 'boolean' ? est.proformaOnApproval : PROFORMA_ON_APPROVAL_ENABLED;
    if (!proformaOnApproval && est && (est.eligible || est.assigned)) { setPromptData({ payload, est }); return; }
    await placeDirect();
  };

  // Prompt action — place without paying an advance now (also used by the not-eligible "Place order").
  const placeWithoutAdvance = async () => {
    if (!promptData) return;
    const r = await submitOrder(promptData.payload);
    if (r) { setPromptData(null); navigate(backTo); }
  };

  // Prompt action (eligible only) — place, mark the order ESTIMATED (choose) so the pay modal charges the
  // net payable, then open the pay modal.
  const payAndPlace = async () => {
    if (!promptData) return;
    const r = await submitOrder(promptData.payload);
    if (!r) return;
    if (r.id) {
      try { await request({ url: `/orders/${r.id}/advance-cd/choose`, method: 'POST' }); }
      catch { /* fall back to paying the full amount if choose fails */ }
    }
    setPromptData(null);
    setPayFor({ id: String(r.id), orderNo: r.orderNo });
  };

  // After the pay modal closes (paid or skipped), leave the New Order page.
  const finish = () => {
    setPromptData(null);
    setPayFor(null);
    navigate(backTo);
  };

  const canPlace = cart.length > 0 && (isRetailer || !!retailerId) && !(exceedsCredit && (!canOverride || !overrideCredit));

  return (
    <div>
      <h1 className="type-page-title text-content">New Order</h1>
      <p className="mt-1 max-w-3xl text-sm text-muted">
        {isRetailer
          ? 'Add products to your cart and place your order. The dispatch warehouse is chosen when the order is approved.'
          : 'Pick a retailer, add products to the cart, and place the order. The dispatch warehouse is chosen when the order is approved.'}
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* ── Catalog ── */}
        <div className="lg:col-span-2">
          <div className="rounded-card border border-line bg-surface shadow-e1">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
              <span className="type-body-strong text-content">Products{productGroups.length ? ` (${productGroups.length})` : ''}</span>
              <div className="flex flex-wrap items-center gap-2">
                <Select allowClear placeholder="UoM" style={{ width: 104 }} value={uomFilter} onChange={setUomFilter} options={uomOptions} />
                <Select
                  style={{ width: 132 }} value={stockFilter} onChange={setStockFilter}
                  options={[
                    { value: 'all', label: 'All stock' },
                    { value: 'in', label: 'In stock' },
                    { value: 'out', label: 'Out of stock' },
                  ]}
                />
                <Input.Search allowClear placeholder="Search by name, SKU or HSN…" aria-label="Search by name, SKU or HSN" style={{ width: 240 }}
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>

            <div className="p-4">
              {catalogLoading ? (
                <div className="py-10 text-center text-sm text-subtle">Loading products…</div>
              ) : productGroups.length === 0 ? (
                <div className="py-12 text-center text-sm text-subtle">{search ? 'No products match your search' : 'No products in the catalog'}</div>
              ) : (
                /* One card per row on a phone: two ~170px cards could not hold "₹180 ₹200 1,080 × 1 L in stock"
                   beside the add button, and the stock figure was clipped at the card edge. */
                <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 sm:grid-cols-3">
                  {productGroups.map((g) => (
                    <ProductGroupCard
                      key={g.key} group={g} cartById={cartById}
                      onAdd={addToCart}
                      onInc={(pid) => setQty(pid, (cartById.get(pid)?.qty ?? 0) + 1)}
                      onDec={(pid) => setQty(pid, (cartById.get(pid)?.qty ?? 0) - 1)}
                      onSetQty={setQty}
                      onBox={setBoxLoose}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Cart ── */}
        <div>
          {/*
            * The cart has NO scrollbar of its own.
            *
            * The items list was capped at `max-h-[360px]`, so a five-line cart grew its own
            * scrollbar while the product grid ran on for another two thousand pixels beside it — a
            * box to scroll inside a page that was already scrolling. It now grows with its contents
            * and ends where the product column does; the page is the only thing that scrolls. The
            * totals bar below keeps the CTA reachable (see its own note).
            */}
          <div className="sticky top-4 flex flex-col rounded-card border border-line bg-surface shadow-e1">
            <div className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-3">
              <ShoppingCart size={16} className="text-brand-600" />
              <span className="type-body-strong text-content">Cart</span>
              {cart.length > 0 && <span className="rounded-pill bg-brand-500/12 px-2 py-px type-label text-brand-700">{cart.length}</span>}
            </div>

            <div className="flex flex-col p-4">
              {isRetailer ? (
                <div className="mb-2.5 text-xs text-muted">Ordering for <b className="text-content">your shop</b>.</div>
              ) : (
                <div className="mb-2.5">
                  <label className="mb-1 block type-label-strong text-content">Retailer <span className="text-danger-600">*</span></label>
                  <Select
                    size="large" placeholder="Select retailer…" style={{ width: '100%' }}
                    value={retailerId} {...CUSTOMER_SELECT_PROPS}
                    disabled={!!visitId} // fixed to the visited retailer when ordering during a visit
                    filterOption={false}
                    onSearch={onRetailerSearch}
                    onChange={(v) => {
                      setRetailerId(v); setOverrideCredit(false);
                      // Re-apply the new retailer's discount policy to items already in the cart: GENERAL →
                      // fixed %; otherwise keep the per-line value only for pricing-authorised users, else 0.
                      const { isGeneral, pct } = policyFor(v);
                      setCart((c) => c.map((l) => ({ ...l, discountPct: isGeneral ? pct : (canOverrideDiscount ? l.discountPct : 0) })));
                    }}
                    options={retailerRows.map((r: any) => {
                      // Deactivated retailers can't be ordered for (backend blocks it) — disable them here too.
                      const blocked = r.status === 'SUSPENDED' || r.status === 'CLOSED';
                      const opt = customerOption(r);
                      if (!blocked) return opt;
                      // Say WHY it cannot be picked, in the row and in the searchable text — a greyed
                      // line with no reason reads as a loading state.
                      const why = r.status === 'CLOSED' ? 'Closed' : 'Suspended';
                      return {
                        ...opt,
                        disabled: true,
                        title: `${opt.title} — ${why}`,
                        label: (
                          <span className="flex min-w-0 items-center justify-between gap-2">
                            {opt.label}
                            <span className="shrink-0 rounded-full bg-danger-50 px-2 py-0.5 type-caption text-danger-700 dark:bg-danger-950 dark:text-danger-300">{why}</span>
                          </span>
                        ),
                      };
                    })}
                  />
                  {visitId && <div className="mt-1 text-2xs text-subtle">Linked to your active field visit — retailer is fixed.</div>}
                </div>
              )}

              {retailer && (
                <div className="mb-2 text-xs text-muted">
                  Available credit: <b className={available > 0 ? 'text-success-600' : 'text-danger-600'}>{inr2(available)}</b>
                  {/* A negative outstanding is money the dealer already has on account — say so instead of printing "− ₹-2,500". */}
                  <span className="text-subtle"> (limit {inr2(creditLimit)} {outstanding < 0 ? `+ ${inr2(-outstanding)} advance on account` : `− ${inr2(outstanding)} outstanding`})</span>
                </div>
              )}
              {isGeneralRetailer && (
                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-pill bg-sky-500/12 px-2 py-px font-medium text-sky-600">General · {generalPct}%</span>
                  {canOverrideDiscount
                    ? <span className="text-sky-600">fixed default — you can override per product</span>
                    : <span className="text-subtle">fixed discount, locked</span>}
                </div>
              )}

              <div className="my-3 border-t border-line" />

              {cart.length === 0 ? (
                <div className="py-10 text-center text-sm text-subtle">Cart is empty — add products</div>
              ) : (
                <div className="mb-2 space-y-2 pr-1">
                  {cart.map((l, i) => {
                    const units = lineUnits(l);
                    const boxed = !!l.boxPackingEnabled && Number(l.unitsPerBox) > 0;
                    const lineGross = units * l.unitPrice; // 'total units × unit price' — the discount is applied once, in the summary below
                    const backorder = units > l.stockTotal;
                    const availBoxLoose = boxed ? displayBoxLoose(l.stockTotal, l.unitsPerBox) : '';
                    const lineTitle = `${l.name}${l.variantLabel ? ` · ${l.variantLabel}` : ''}`;
                    // A cart quantity is a COUNT OF PACKS: 250 bottles of 500 ml read "250 LTR × ₹180" and
                    // "₹180/LTR" (strict test 28 Sep). Name the pack instead — "250 × 500 ml", "₹180 per 500 ml".
                    const packs = (n: number) => (l.packSize ? fmtPackQty(n, l.packSize, l.uom) : `${fmtQty(n)} ${l.uom}`);
                    const perPack = l.packSize ? fmtUom(l.packSize, l.uom) : l.uom;
                    return (
                      // Each line is its OWN block, not a hairline-separated run. A box-packed line trails a
                      // "2 boxes × 10 = 20 LTR · available …" hint, and across a shared border that hint read
                      // as a heading for the NEXT product. A card boundary makes every sub-line visibly
                      // belong to exactly one product.
                      <div key={l.productId} className="rounded-card border border-line bg-surface-2/40 p-2.5">
                        <div className="flex items-start gap-2.5">
                          {/* Line number — the one mark on the row that VARIES, so each product has an anchor
                              to point at ("line 3") instead of four look-alike blocks of text. */}
                          <span className="mt-px grid size-5 shrink-0 place-items-center rounded-md bg-brand-500/12 type-caption font-bold tabular-nums text-brand-700 dark:text-brand-300">{i + 1}</span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate type-body-medium text-content">{lineTitle}</div>
                            <div className="text-2xs text-subtle">
                              {l.sku} · {inr2(l.unitPrice)} per {perPack}
                              {boxed && <span className="text-subtle"> · {fmtQty(l.unitsPerBox)} units/box</span>}
                            </div>
                          </div>
                          <button
                            onClick={() => removeLine(l.productId)}
                            title="Remove"
                            aria-label={`Remove ${lineTitle} from the cart`}
                            className="shrink-0 rounded p-1 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 size={16} color="#dc2626" />
                          </button>
                        </div>
                        {/* Quantity, discount, money and the stock hints stay in the number's column, so they
                            read as this line's detail rather than as free-floating text between products. */}
                        <div className="pl-7.5">
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                          {boxed ? (
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-1">
                                <span className="type-caption text-muted">Boxes</span>
                                <InputNumber size="small" min={0} step={1} precision={0} value={l.boxQty ?? 0} controls={false}
                                  onChange={(v) => setBoxLoose(l.productId, Number(v ?? 0), l.looseQty ?? 0)} style={{ width: 52 }} />
                              </label>
                              <span className="text-muted">+</span>
                              <label className="flex items-center gap-1">
                                <span className="type-caption text-muted">Loose</span>
                                <InputNumber size="small" min={0} step={1} value={l.looseQty ?? 0} controls={false}
                                  onChange={(v) => setBoxLoose(l.productId, l.boxQty ?? 0, Number(v ?? 0))} style={{ width: 52 }} />
                              </label>
                            </div>
                          ) : (
                            <Stepper qty={l.qty} onDec={() => setQty(l.productId, l.qty - 1)} onInc={() => setQty(l.productId, l.qty + 1)} onSet={(v) => setQty(l.productId, v)} />
                          )}
                          {/* Line discount — only for pricing-authorised users; hidden entirely otherwise */}
                          {canOverrideDiscount && (
                            <label className="flex items-center gap-1.5">
                              <span className="type-caption text-muted">Discount</span>
                              <InputNumber
                                size="small" min={0} max={100} value={l.discountPct} controls={false}
                                onChange={(v) => setDisc(l.productId, Number(v ?? 0))} style={{ width: 56 }}
                              />
                              <span className="text-2xs text-muted">%</span>
                            </label>
                          )}
                          <div className="text-right">
                            <b className="block whitespace-nowrap tabular-nums text-content">{inr2(lineGross)}</b>
                            <div className="whitespace-nowrap text-2xs tabular-nums text-subtle">{packs(units)} @ {inr2(l.unitPrice)}</div>
                          </div>
                        </div>
                        {boxed && <div className="mt-1 text-2xs text-subtle"><b className="text-content">{boxLoosePhrase(l.boxQty, l.looseQty, l.unitsPerBox)}</b> = {packs(units)}{availBoxLoose ? ` · available ${availBoxLoose}` : ''}</div>}
                        {backorder && <div className="mt-1 text-2xs text-warning-600">Only {packs(l.stockTotal)} in stock — rest will be backordered</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {cart.length > 0 && (
                /*
                  * The cart no longer scrolls inside itself — it grows with its contents and the PAGE
                  * scrolls, so the panel ends where the product grid does instead of boxing a
                  * scrollbar inside a page that is already scrolling.
                  *
                  * The price of that is the Place Order button drifting far down a long cart, so the
                  * totals block is `sticky bottom-0`: it rides the bottom of the viewport for as long
                  * as any part of the cart is on screen, and settles into place at the end. The
                  * negative margins + padding let the bar's background span the panel's full width
                  * while the panel keeps its own padding.
                  */
                <div className="sticky bottom-0 -mx-4 -mb-4 mt-1 border-t border-line bg-surface px-4 pb-4 pt-3">
                  <div className="text-sm leading-6">
                    <div className="flex justify-between">
                      <span className="text-muted">Items total</span>
                      <span className="tabular-nums text-content">{inr2(subtotal)}</span>
                    </div>
                    {(canOverrideDiscount || discount > 0) && (
                      <div className="flex justify-between">
                        <span className="text-muted">Discount{discountPctEff > 0 ? ` (${discountPctEff}% off)` : ''}</span>
                        <span className="tabular-nums text-success-600">−{inr2(discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted">GST{uniformGstPct != null ? ` (${uniformGstPct}%)` : ' (tax)'}</span>
                      <span className="tabular-nums text-content">+{inr2(gst)}</span>
                    </div>
                    {Math.abs(roundOff) >= 0.005 && (
                      <div className="flex justify-between">
                        <span className="text-muted">Round off</span>
                        <span className="tabular-nums text-content">{roundOff >= 0 ? '+' : '−'}{inr2(Math.abs(roundOff))}</span>
                      </div>
                    )}
                    <div className="my-2 border-t border-line" />
                    <div className="flex justify-between type-section-title">
                      <span className="text-content">Total to pay</span>
                      <span className="tabular-nums text-brand-700">{inr2(grandTotal)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="mt-2 rounded-control bg-success-500/10 px-2.5 py-1.5 type-caption text-success-600">
                        ✓ You save {inr2(discount)} with your {discountPctEff > 0 ? `${discountPctEff}% ` : ''}dealer discount
                      </div>
                    )}
                  </div>

                  {/* Override discount → a reason is required, so staff AND the retailer can later see WHY the
                      discount was given (stored on the order + printed on the invoice / proforma). */}
                  {isOverrideDiscount && (
                    <div className="mt-3 rounded-control border border-warning-500/40 bg-warning-500/10 p-3 text-xs">
                      <div className="font-semibold text-warning-700">Discount reason required</div>
                      <div className="mt-0.5 text-muted">This discount goes beyond the standard policy — record why. It's shown on the order and the invoice/proforma the retailer sees.</div>
                      <textarea
                        className="mt-2 w-full resize-none rounded-control border border-line bg-surface px-2.5 py-1.5 text-xs text-content outline-none focus:border-brand-500"
                        rows={2}
                        maxLength={500}
                        value={discountReason}
                        onChange={(e) => setDiscountReason(e.target.value)}
                        placeholder="e.g. Bulk deal approved by Sales Head · price match · damaged-stock adjustment"
                      />
                    </div>
                  )}

                  {exceedsCredit && (
                    canOverride ? (
                      <div className="mt-3 rounded-control border border-warning-500/40 bg-warning-500/10 p-3 text-xs">
                        <div className="font-semibold text-warning-700">Exceeds available credit</div>
                        <div className="mt-0.5 text-muted">Available {inr2(available)} · order ≈ {inr2(grandTotal)}.</div>
                        <div className="mt-1.5"><Checkbox checked={overrideCredit} onCheckedChange={(checked) => setOverrideCredit(checked)} label="Override & place anyway" /></div>
                      </div>
                    ) : (
                      <div className="mt-3 rounded-control border border-danger-500/40 bg-danger-500/10 p-3 text-xs">
                        <div className="font-semibold text-danger-600">Exceeds available credit</div>
                        <div className="mt-0.5 text-muted">Available {inr2(available)} · order ≈ {inr2(grandTotal)}. You need credit-override permission.</div>
                      </div>
                    )
                  )}

                  {/*
                    * What this cart is worth under the dealer's own scheme, live as it changes.
                    *
                    * The advance-CD prompt only appears AFTER Place Order, by which point the cart is
                    * already decided — too late to be the reason anyone adds a line. This says what
                    * prepaying would save and how far the year-end step is while there is still
                    * something to change. Renders nothing for a staff user, who has no scheme.
                    */}
                  <CartEarningPanel cartValue={grandTotal} taxableValue={taxable} />

                  <Button variant="primary" size="lg" block style={{ marginTop: 12 }}
                    loading={placing} disabled={!canPlace} onClick={place}>
                    Place Order · {inr2(grandTotal)}
                  </Button>
                  {!isRetailer && !retailerId && <div className="mt-1.5 text-center text-xs text-subtle">Select a retailer to place the order</div>}
                  {/* A retailer on no scheme has no cash discount to confirm; staff see it (the dealer may have one). */}
                  {canPayOrder && (!isRetailer || !!myEarnings) && <div className="mt-1.5 text-center text-2xs text-subtle">Any advance cash discount is confirmed before billing.</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Place-Order prompt (payers): previews the advance cash discount for the cart and creates the order
          only on an explicit choice — cancelling places nothing. Pay & place → PENDING → verify → lock. */}
      <AdvancePayPromptModal
        est={promptData?.est ?? null}
        summary={{ subtotal, gstPct: uniformGstPct }}
        placing={placing}
        onCancel={() => setPromptData(null)}
        onPlace={placeWithoutAdvance}
        onPayAndPlace={payAndPlace}
      />
      {payFor && <PayOrderModal open onClose={finish} orderId={payFor.id} orderNo={payFor.orderNo} />}
    </div>
  );
}

function Stepper({ qty, onDec, onInc, onSet }: { qty: number; onDec: () => void; onInc: () => void; onSet: (v: number) => void }) {
  return (
    <div className="inline-flex items-center overflow-hidden rounded-control border border-line">
      <button type="button" onClick={onDec} className="px-2 py-1 text-muted hover:bg-surface-2" aria-label="Decrease"><Minus size={16} /></button>
      <input
        type="number" min={1} value={qty}
        onChange={(e) => onSet(Number(e.target.value || 1))}
        className="w-10 border-x border-line bg-transparent py-1 text-center text-sm text-content outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <button type="button" onClick={onInc} className="px-2 py-1 text-muted hover:bg-surface-2" aria-label="Increase"><Plus size={16} /></button>
    </div>
  );
}

// One compact card per product. A single-variant product shows its price + control
// directly; a multi-variant product lists each variant as a small add/stepper row.
function ProductGroupCard({ group, cartById, onAdd, onInc, onDec, onSetQty, onBox }: {
  group: { key: string; name: string; imageUrl?: string; uom: string; variants: any[] };
  cartById: Map<string, CartLine>;
  onAdd: (p: any) => void;
  onInc: (pid: string) => void;
  onDec: (pid: string) => void;
  onSetQty: (pid: string, v: number) => void;
  onBox: (pid: string, boxQty: number, looseQty: number) => void;
}) {
  const multi = group.variants.length > 1;
  const anyInCart = group.variants.some((v) => cartById.has(v.productId));

  const VariantRow = ({ v, showLabel }: { v: any; showLabel: boolean }) => {
    const line = cartById.get(v.productId);
    const stock = Number(v.stock?.total ?? 0);
    const discounted = Number(v.mrp) > Number(v.price);
    return (
      <div className="flex items-center justify-between gap-1.5 py-1">
        <div className="min-w-0">
          {showLabel && <div className="truncate type-label text-content">{Number(v.packSize) > 0 ? fmtUom(v.packSize, group.uom) : (v.variantLabel || v.sku)}</div>}
          <div className="text-xs">
            <b className="text-brand-700">{inr2(Number(v.price))}</b>
            {discounted && <span className="ml-1 text-2xs text-subtle line-through">{inr(Number(v.mrp))}</span>}
            {/* Its own line: beside the price it was the part that got cut off on narrow cards. */}
            <span className={`block text-2xs ${stock > 0 ? 'text-subtle' : 'text-warning-600'}`}>
              {stock > 0 ? `${fmtPackQty(stock, v.packSize, group.uom)} in stock` : 'Made to order'}
            </span>
          </div>
        </div>
        {line
          ? line.boxPackingEnabled
            // Box-packed line: the quick stepper adjusts BOXES (qty is derived), wired to the same
            // setBoxLoose the cart panel uses — so it's connected and a decrement can't silently
            // delete the line at box 1 (0 boxes + existing loose is a valid state).
            ? (
              <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                <Stepper
                  qty={line.boxQty ?? 0}
                  onDec={() => onBox(v.productId, (line.boxQty ?? 0) - 1, line.looseQty ?? 0)}
                  onInc={() => onBox(v.productId, (line.boxQty ?? 0) + 1, line.looseQty ?? 0)}
                  onSet={(q) => onBox(v.productId, q, line.looseQty ?? 0)}
                />
                <span className="text-2xs text-subtle">box</span>
              </div>
            )
            : <Stepper qty={line.qty} onDec={() => onDec(v.productId)} onInc={() => onInc(v.productId)} onSet={(q) => onSetQty(v.productId, q)} />
          : <Button size="sm" variant="ghost" aria-label="Add" icon={<Plus size={16} />} onClick={() => onAdd(v)} style={{ flexShrink: 0 }} />}
      </div>
    );
  };

  // All images for this product: every variant's gallery (imageUrls) + cover, deduped.
  const images: string[] = [];
  const seen = new Set<string>();
  const push = (u?: string | null) => { if (u && !seen.has(u)) { seen.add(u); images.push(u); } };
  group.variants.forEach((v) => {
    const gallery = toImageUrls(v.imageUrls);
    if (gallery.length) gallery.forEach((u) => push(u));
    else push(v.imageUrl);
  });
  if (!images.length) push(group.imageUrl);

  const IMG_H = 130;
  const imgStyle = { height: IMG_H, objectFit: 'contain' as const, width: '100%' };
  const cover = (
    <div className="relative bg-surface-2" style={{ height: IMG_H }}>
      {images.length === 0 ? (
        <div className="flex h-full items-center justify-center"><ImageIcon size={24} className="text-subtle" /></div>
      ) : (
        <Image.PreviewGroup>
          {images.length === 1 ? (
            <Image src={images[0]} style={imgStyle} rootClassName="gb-prod-img"
              preview={{ mask: <span><Eye size={16} /> Preview</span> }} />
          ) : (
            <Carousel arrows dots draggable adaptiveHeight={false}>
              {images.map((src, i) => (
                <div key={i}>
                  <Image src={src} style={imgStyle} rootClassName="gb-prod-img"
                    preview={{ mask: <span><Eye size={16} /> Preview</span> }} />
                </div>
              ))}
            </Carousel>
          )}
        </Image.PreviewGroup>
      )}
    </div>
  );

  return (
    <div className={`flex h-full flex-col overflow-hidden rounded-card border bg-surface shadow-soft transition hover:shadow-card ${anyInCart ? 'border-brand-600' : 'border-line'}`}>
      {cover}
      <div className="p-2.5">
        <div className="mb-1 flex items-center gap-1.5">
          <Tooltip title={group.name}>
            <span className="truncate type-body-strong text-content">{group.name}</span>
          </Tooltip>
          {multi && <span className="shrink-0 rounded-pill bg-sky-500/12 px-1.5 py-px type-caption text-sky-600">{group.variants.length} variants</span>}
        </div>
        <div>
          {group.variants.map((v, i) => (
            <div key={v.productId} className={i ? 'border-t border-line' : ''}>
              <VariantRow v={v} showLabel={multi} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
